import React, { useRef } from "react";
import { motion } from "framer-motion";
import FeatureSection from "./FeatureSection";
import BenefitsSection from "./BenefitsSection";
import CallToActionSection from "./CallToActionSection";
import TheyTalkAboutUsSection from "./TheyTalkAboutUsSection";
import TrustedBySection from "./TrustedBySection";
import PricingSection from "./PricingSection";
import FAQSection from "./FAQSection";
import FinalCTA from "./FinalCTA";
import { benefits, features } from "./heroData";

const ScrollParallaxHero: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  const mediaData = [
    {
      logo: "/images/frenchjournal.jpg",
      link: "https://frenchtechjournal.com/shreds-ai-genai-coding-economics/",
      alt: "French Tech Journal article featuring Shreds.AI, interviewing the founder Soufiane AMAR about how Shreds.AI is revolutionizing the software development industry.",
    },
    {
      logo: "/images/devops.jpg",
      link: "https://devops.com/shreds-ai-launches-llm-platform-trained-for-software-engineering/",
      alt: "DevOps article featuring Shreds.AI, announcing that Shreds.AI cracked automated software development.",
    },
    {
      logo: "/images/forbes.png",
      link: "https://www.forbes.com/sites/adrianbridgwater/2024/08/09/the-human-face-of-ai-software-code-assistants/",
      alt: "Forbes article featuring Shreds.AI, talking about how Shreds.AI revolutionizes the software development industry, by automating software development.",
    },
    {
      logo: "/images/silliconangle.jpg",
      link: "https://siliconangle.com/2024/06/13/startup-claims-can-automate-80-software-development-generative-ai/",
      alt: "SiliconAngle article featuring Shreds.AI, annouing Shreds.AI lance, an AI that can automate 90% of software development.",
    },
    {
      logo: "/images/thenewstack.jpg",
      link: "https://thenewstack.io/shreds-ai-creates-complex-software-in-java-and-javascript/",
      alt: "The New Stack article featuring Shreds.AI, talking about how Shreds.AI can create complex software in Java and JavaScript.",
    },
    {
      logo: "/images/aithority.jpg",
      link: "https://aithority.com/tag/shreds-ai/",
      alt: "AI Authority article featuring Shreds.AI, talking about the latest news and updates about Shreds.AI."
    },
    {
      logo: "/images/insideainews.jpg",
      link: "https://insideainews.com/2024/06/17/shreds-ai-launches-an-ai-capable-of-generating-complex-software/",
      alt: "Inside AI News article featuring Shreds.AI, talking about the launch of Shreds.AI, an AI capable of generating complex software.",
    }
  ];

  return (
    <div ref={containerRef} className="relative bg-gradient-to-br from-gray-950 via-gray-900 to-black">
      <div id="features" className="relative space-y-4 md:space-y-6">
        {features.map((feature, index) => (
          <div key={index} className="relative">
            {index > 0 && (
              <motion.div
                className="absolute inset-x-0 -top-8 h-16 pointer-events-none"
                style={{
                  background: `linear-gradient(to bottom, 
                    ${features[index - 1]?.color}05 0%, 
                    transparent 50%, 
                    ${feature.color}05 100%)`
                }}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 1, ease: "easeInOut" }}
                viewport={{ once: false, margin: "-10%" }}
              />
            )}
            
            <FeatureSection
              feature={feature}
              index={index}
              isActive={true}
            />
          </div>
        ))}
      </div>

      <motion.div 
        className="relative py-20 overflow-hidden"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 1.5 }}
        viewport={{ once: false, margin: "-10%" }}
      >
        <motion.div 
          className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-500/5 to-green-500/5"
          initial={{ scaleY: 0 }}
          whileInView={{ scaleY: 1 }}
          transition={{ duration: 2, ease: "easeInOut" }}
          viewport={{ once: false }}
        />
        
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={`transition-particle-${i}`}
            className="absolute rounded-full bg-gradient-to-r from-blue-400/20 to-green-400/20"
            style={{
              width: Math.random() * 8 + 4,
              height: Math.random() * 8 + 4,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -100, 0],
              opacity: [0, 0.6, 0],
              scale: [0.5, 1.2, 0.5],
            }}
            transition={{
              duration: Math.random() * 4 + 3,
              repeat: Infinity,
              delay: Math.random() * 2,
              ease: "easeInOut"
            }}
          />
        ))}
        
        <motion.div 
          className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-1 bg-gradient-to-b from-purple-400 via-blue-400 to-green-400 rounded-full"
          initial={{ height: 0, opacity: 0 }}
          whileInView={{ height: "200px", opacity: 0.6 }}
          transition={{ duration: 1.5, delay: 0.5 }}
          viewport={{ once: false }}
        />
      </motion.div>

      <BenefitsSection benefits={benefits} />

      <CallToActionSection />

      <motion.div 
        className="relative py-20 overflow-hidden"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 1.5 }}
        viewport={{ once: false, margin: "-10%" }}
      >
        <motion.div 
          className="absolute inset-0 bg-gradient-to-b from-green-500/5 via-purple-500/10 to-amber-500/5"
          initial={{ scaleY: 0 }}
          whileInView={{ scaleY: 1 }}
          transition={{ duration: 2, ease: "easeInOut" }}
          viewport={{ once: false }}
        />
        
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={`benefits-to-trusted-particle-${i}`}
            className="absolute rounded-full bg-gradient-to-r from-green-400/20 to-amber-400/20"
            style={{
              width: Math.random() * 8 + 4,
              height: Math.random() * 8 + 4,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -100, 0],
              opacity: [0, 0.6, 0],
              scale: [0.5, 1.2, 0.5],
            }}
            transition={{
              duration: Math.random() * 4 + 3,
              repeat: Infinity,
              delay: Math.random() * 2,
              ease: "easeInOut"
            }}
          />
        ))}
        
        <motion.div 
          className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-1 bg-gradient-to-b from-green-400 via-purple-400 to-amber-400 rounded-full"
          initial={{ height: 0, opacity: 0 }}
          whileInView={{ height: "200px", opacity: 0.6 }}
          transition={{ duration: 1.5, delay: 0.5 }}
          viewport={{ once: false }}
        />
      </motion.div>

      <TrustedBySection />
      
      <PricingSection />
      
      <TheyTalkAboutUsSection mediaData={mediaData} />
      
      <FAQSection />
      
      <FinalCTA />
    </div>
  );
};

export default ScrollParallaxHero;